import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from 'sonner';
import '@/App.css';
import CitySelection from '@/pages/CitySelection';
import Search from '@/pages/Search';
import Results from '@/pages/Results';
import ReportPrice from '@/pages/ReportPrice';
import Profile from '@/pages/Profile';
import Auth from '@/pages/Auth';
import AdminModeration from '@/pages/AdminModeration';
import Explore from '@/pages/Explore';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import CommunityGuidelines from '@/pages/CommunityGuidelines';
import ReportIssue from '@/pages/ReportIssue';
import Partner from '@/pages/Partner';
import Footer from '@/components/Footer';
import { hideEmergentBadge } from '@/lib/emergent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [selectedCity, setSelectedCity] = useState(
    JSON.parse(localStorage.getItem('selectedCity') || 'null')
  );
  const [userLocation, setUserLocation] = useState(() => {
    try {
      const s = localStorage.getItem('userLocation');
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Failed to fetch user', error);
    }
  };

  const refreshUser = () => {
    if (token) {
      fetchUser();
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const selectCity = (city) => {
    localStorage.setItem('selectedCity', JSON.stringify(city));
    setSelectedCity(city);
  };

  useEffect(() => {
    if (token) {
      fetchUser();
    }
    // We intentionally omit fetchUser from deps: it only depends on stable API + token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    hideEmergentBadge();
    const interval = setInterval(hideEmergentBadge, 1000);
    return () => clearInterval(interval);
  }, []);

  const setUserLocationAndPersist = (loc) => {
    setUserLocation(loc);
    if (loc) localStorage.setItem('userLocation', JSON.stringify(loc));
    else localStorage.removeItem('userLocation');
  };

  return (
    <ThemeProvider attribute="class" defaultTheme="light">
      <AuthContext.Provider value={{ token, user, login, logout, selectedCity, selectCity, refreshUser, userLocation, setUserLocation: setUserLocationAndPersist }}>
        <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/report-issue" element={<ReportIssue />} />
            <Route path="/partner" element={<Partner />} />
            <Route
              path="/"
              element={
                selectedCity ? <Navigate to="/search" /> : <CitySelection />
              }
            />
            <Route path="/search" element={<Search />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/results" element={<Results />} />
            <Route
              path="/report"
              element={token ? <ReportPrice /> : <Navigate to="/auth" />}
            />
            <Route
              path="/profile"
              element={token ? <Profile /> : <Navigate to="/auth" />}
            />
            <Route
              path="/admin"
              element={token ? <AdminModeration /> : <Navigate to="/auth" />}
            />
          </Routes>
          <Footer />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;